<template>
  <section class="keywords-phone-category">
    <!-- While selecting, the title line, the category's count line, is the selection bar. -->
    <div v-if="selecting" class="keywords-phone-category__header is-selecting">
      <FormCheckbox
        :indeterminate="someSelected"
        :afterIndeterminate="false"
        :onChange="toggleAll"
        :value="allSelected"
        :aria-label="$t('table.phone-list.select-all')"
      />
      <span class="keywords-phone-category__selected">
        {{ $tc('table.phone-list.selected', selected.length, { count: selected.length }) }}
      </span>
      <BaseButton v-if="selected.length" is-small is-danger has-icon-left @click.native="deleteSelected">
        <BaseIcon name="trash-2" />
        {{ $t('actions.delete') }}
      </BaseButton>
      <BaseRoundButton
        :size="44"
        icon="x"
        is-transparent
        :aria-label="$t('actions.cancel')"
        aria-pressed="true"
        @click.native="stopSelecting"
      />
    </div>
    <div v-else class="keywords-phone-category__header">
      <span
        v-if="category.colorCode"
        class="keywords-phone-category__color"
        :style="{ background: category.colorCode }"
        aria-hidden="true"
      />
      <h2 class="keywords-phone-category__name">{{ category.name }}</h2>
      <span class="keywords-phone-category__count number-monospace">{{ keywords.length }}</span>
      <span class="keywords-phone-category__spacer" />
      <BaseRoundButton
        v-if="keywords.length"
        :size="44"
        icon="check-square"
        is-transparent
        :aria-label="$t('table.phone-list.select')"
        aria-pressed="false"
        @click.native="selecting = true"
      />
      <BaseRoundButton
        :size="44"
        icon="more-horizontal"
        is-transparent
        :aria-label="$t('actions.more-actions')"
        @click.native="categorySheetOpen = true"
      />
    </div>

    <ul v-if="keywords.length" class="keywords-phone-category__lines">
      <BasePhoneRow
        v-for="keyword in listed"
        :key="keyword.id"
        opens-dialog
        :selectable="selecting"
        :selected="isSelected(keyword)"
        @open="$emit('edit-keyword', keyword)"
        @toggle-select="toggle(keyword)"
      >
        <template #title>
          <!-- Keyword's own markup (divs, a hover popover) can't go in the row's button. -->
          <span class="keyword base-phone-row__title">
            <BaseIcon name="arrow-right" class="arrow" />
            <span v-html="highlight(keyword.value)" />
          </span>
        </template>
        <template v-if="!selecting" #actions>
          <button
            type="button"
            class="base-phone-row__action"
            :aria-label="$t('actions.more-actions')"
            aria-haspopup="dialog"
            @click.stop="sheetKeyword = keyword"
          >
            <BaseIcon name="more-horizontal" aria-hidden="true" />
          </button>
        </template>
      </BasePhoneRow>
    </ul>
    <button v-if="hasMore" type="button" class="keywords-phone-category__more" @click="showAll = !showAll">
      {{ showAll ? $t('dialog.keywords.hide') : $t('dialog.keywords.show-more', { count: keywords.length - limit }) }}
    </button>
    <div v-if="!keywords.length" class="keywords-phone-category__empty">
      <BaseButton is-small has-icon-left @click.native="$emit('add')">
        <BaseIcon name="plus" />
        {{ $tc('page.admin-keywords.add-keywords', 0) }}
      </BaseButton>
    </div>

    <BaseBottomSheet v-model="categorySheetOpen" :title="category.name">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet(() => $emit('add'))">
            <BaseIcon name="plus" />
            {{ $tc('page.admin-keywords.add-keywords', 0) }}
          </button>
        </li>
        <li>
          <button type="button" @click="fromSheet(() => $emit('edit-category'))">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li>
          <button type="button" class="is-danger" @click="fromSheet(() => $emit('delete-category'))">
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
    <BaseBottomSheet v-model="keywordSheetOpen" :title="sheetKeyword ? sheetKeyword.value : ''">
      <ul v-if="sheetKeyword" class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet(() => $emit('edit-keyword', sheetKeyword))">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li>
          <button type="button" class="is-danger" @click="fromSheet(() => $emit('delete-keywords', [sheetKeyword]))">
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </section>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import { highlightKeywordSyntax } from '@/util/keyword-syntax-highlight';

// A category of keywords on a phone (search keywords and document keywords alike): a line per
// keyword, which opens it, the category's actions in its ⋯, and a selection to delete several at
// once from its title line, as above the other lists. Larger screens keep the category's table.
export default {
  name: 'KeywordsPhoneCategory',
  components: { BaseBottomSheet, BaseButton, BaseIcon, BasePhoneRow, BaseRoundButton, FormCheckbox },
  props: {
    // `{ name, colorCode? }`
    category: { type: Object, required: true },
    keywords: { type: Array, default: () => [] },
    // As many as the table shows before its "show more".
    limit: { type: Number, default: 8 },
  },
  data() {
    return {
      categorySheetOpen: false,
      sheetKeyword: null,
      selecting: false,
      selected: [],
      showAll: false,
    };
  },
  computed: {
    // While selecting, every keyword shows: "select all" ticks the hidden ones too.
    listed() {
      return this.showAll || this.selecting ? this.keywords : this.keywords.slice(0, this.limit);
    },
    hasMore() {
      return !this.selecting && this.keywords.length > this.limit;
    },
    allSelected() {
      return this.keywords.length > 0 && this.selected.length === this.keywords.length;
    },
    someSelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
    keywordSheetOpen: {
      get() {
        return !!this.sheetKeyword;
      },
      set(open) {
        if (!open) this.sheetKeyword = null;
      },
    },
  },
  watch: {
    // Keywords deleted or edited elsewhere leave the selection.
    keywords() {
      const ids = new Set(this.keywords.map((keyword) => keyword.id));
      this.selected = this.selected.filter((keyword) => ids.has(keyword.id));
    },
  },
  methods: {
    highlight: highlightKeywordSyntax,
    // As in every sheet, a choice closes it first.
    fromSheet(action) {
      this.categorySheetOpen = false;
      action();
      this.sheetKeyword = null;
    },
    isSelected(keyword) {
      return this.selected.some((k) => k.id === keyword.id);
    },
    toggle(keyword) {
      this.selected = this.isSelected(keyword)
        ? this.selected.filter((k) => k.id !== keyword.id)
        : [...this.selected, keyword];
    },
    toggleAll() {
      this.selected = this.allSelected || this.someSelected ? [] : [...this.keywords];
    },
    deleteSelected() {
      this.$emit('delete-keywords', [...this.selected]);
      this.stopSelecting();
    },
    stopSelecting() {
      this.selecting = false;
      this.selected = [];
    },
  },
};
</script>

<style lang="scss">
.keywords-phone-category {
  margin-bottom: $space-md;
  background: $color-white;
  border: 1px solid $color-border;
  border-radius: $radius-md;

  &__header {
    position: sticky;
    // Under the navigation bar while it shows, at the top once it hides (the page scrolls under it).
    top: $menu-top-bar-height;
    @include top-bar-follower;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: $space-2;
    min-height: 52px;
    padding: $space-1 $space-1 $space-1 $space-md;
    background: $color-white;
    border-bottom: 1px solid $color-border;
    border-radius: $radius-md $radius-md 0 0;

    &.is-selecting {
      padding-left: $space-3;
    }
  }

  &__color {
    flex: none;
    width: 12px;
    height: 12px;
    border-radius: $radius-full;
  }

  &__name {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: $font-family-heading;
    @include text-style('heading-sm');
    color: $color-text-title;
  }

  &__count {
    flex: none;
    @include text-style('body-sm');
    color: $color-text-muted;
  }

  &__spacer {
    flex: 1;
  }

  &__selected {
    flex: 1;
    min-width: 0;
    @include text-style('body-sm');
    font-weight: $font-weight-medium;
    color: $color-text-title;
  }

  &__lines {
    margin: 0;
    padding: 0;

    > .base-phone-row:last-child {
      border-bottom: 0;
    }

    // The row's title, not the desktop keyword's chip (Keyword.vue: 14px, its padding).
    .keyword.base-phone-row__title {
      padding: 0;
      font-size: 15px;
    }
  }

  &__more {
    display: block;
    width: 100%;
    min-height: 44px;
    border-top: 1px solid $color-border;
    font-size: 14px;
    color: $color-accent;
  }

  &__empty {
    display: flex;
    justify-content: center;
    padding: $space-lg $space-md;
  }
}
</style>
